import type { VoidFunctionComponent } from 'react';

import { plural } from '../../../../shared/helpers';
import { useDeleteMalwareReport } from '../../../hooks/api/malware/index';
import Button, { BUTTON_TYPE, IconButton } from '../../generic/Button';
import CopyToClipboard from '../../generic/CopyToClipboard';
import Flex from '../../generic/Flex';
import { icons } from '../../generic/Icon';
import ExternalLink from '../../generic/Link/ExternalLink';
import List from '../../generic/List';
import Tooltip from '../../generic/Tooltip';
import type { MalwareReport } from '../../../../shared/model-typings/malwarereport';
import type { MalwareRevision } from '../../../../shared/model-typings/malwarerevision';
import type { AlertDialogProps, DialogItemProps } from '../../../contexts/DialogContext';

import styles from './MalwareTable.module.less';

const MalwareRevisionsList:VoidFunctionComponent<{ list: MalwareRevision[] }> = ({ list }) => {
  return (
    <List>
      <List.Header>
        <List.HeaderText>
          {`${list.length} ${plural(list.length, 'locator')}`}
        </List.HeaderText>
      </List.Header>
      <List.Body>
        <List.RowContainer>
          {list.map((revision) => (
            <div key={revision.id} className={styles.listRow}>
              <List.Row isInteractive={false}>
                <CopyToClipboard text={revision.dependencyRevisionLocator}>
                  <code>{revision.dependencyRevisionLocator}</code>
                </CopyToClipboard>
              </List.Row>
            </div>
          ))}
        </List.RowContainer>
      </List.Body>
    </List>
  );
};

type LocatorsCellProps = {
  row: {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- _original is a property from the react-table that contains the original row data
    _original: MalwareReport;
  };
  onShowLocators: (dialogItem: DialogItemProps<AlertDialogProps>) => void;
};

export const LocatorsCell: VoidFunctionComponent<LocatorsCellProps> = ({ row, onShowLocators }) => {
  // eslint-disable-next-line no-underscore-dangle -- _original is a property from the react-table that contains the original row data
  const list = row._original.malwareRevisions;
  if (!list || list.length === 0) {
    return (<div>No locators found.</div>);
  }

  return (
    <Button
      onClick={() => {
        onShowLocators({
          // eslint-disable-next-line no-underscore-dangle -- _original is a property from the react-table that contains the original row data
          title: row._original.name,
          body: (<MalwareRevisionsList list={list} />),
        });
      }}
      type={BUTTON_TYPE.TEXT}>
      {`${list.length} ${plural(list.length, 'locator')}`}
    </Button>
  );
};

export const UrlCell: VoidFunctionComponent<{ value: string }> = ({ value }) => {
  return (<ExternalLink to={value} alignIcon="right" openInNewTab />);
};

type ActionsCellProps = {
  row: {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- _original is a property from the react-table that contains the original row data
    _original: MalwareReport;
  };
  onEditMalware: (malwareReport: MalwareReport) => void;
};

export const ActionsCell: VoidFunctionComponent<ActionsCellProps> = ({ row, onEditMalware }) => {
  const { onDeleteMalwareReport } = useDeleteMalwareReport();

  const handleEdit = () => {
    // eslint-disable-next-line no-underscore-dangle -- _original is a property from the react-table that contains the original row data
    onEditMalware(row._original);
  };

  const handleDelete = () => {
    // eslint-disable-next-line no-underscore-dangle -- _original is a property from the react-table that contains the original row data
    onDeleteMalwareReport(row._original);
  };

  return (
    <Flex align="center" justify="space-around" className="width-full">
      <Tooltip
        // eslint-disable-next-line no-underscore-dangle -- _original is a property from the react-table that contains the original row data
        id={`edit-malware-${row._original.id}`}
        tooltip="Edit malware report"
        placement="left"
        showTooltip
      >
        <span className="inline-block">
          <IconButton
            // eslint-disable-next-line no-underscore-dangle -- _original is a property from the react-table that contains the original row data
            id={`edit-malware-${row._original.id}-button`}
            icon={icons.edit}
            onClick={handleEdit}
          />
        </span>
      </Tooltip>
      <Tooltip
        // eslint-disable-next-line no-underscore-dangle -- _original is a property from the react-table that contains the original row data
        id={`delete-malware-${row._original.id}`}
        tooltip="Delete malware report"
        placement="left"
        showTooltip
      >
        <span className="inline-block">
          <IconButton
            // eslint-disable-next-line no-underscore-dangle -- _original is a property from the react-table that contains the original row data
            id={`delete-malware-${row._original.id}-button`}
            icon={icons.trashRegular}
            onClick={handleDelete}
          />
        </span>
      </Tooltip>
    </Flex>
  );
};
